import React from 'react'
import {Caption} from '@modifi/ui'
import {connect} from '@modifi/redux'
import {selectSearchObj} from '@modifi/router'
import {formatDate, useTranslation} from '@modifi/plugin-i18n'
import moment from 'moment'

import {DropdownArrow, Wrapper} from './styles'
import {BadgeIcon} from '../../../../ui/table/clear-filter/styles'
import {
  fourteenDaysFromToday,
  oneWeekAgo,
  thirtyDaysFromToday,
  today,
} from '../../../../utils/formatMomentToString'
import {getTaskGroupOptions} from '../../../../components/filters/TaskGroupFilter'
import {getSelectedTaskGroupOptions} from '../../../../components/filters/CategoryFilter'

const formattedDate = (date: string) => formatDate(navigator.language || 'en_GB', date)

export type InfoDropdownHeaderProps = {
  search?: Record<string, any> | null
  filterType: 'date' | 'status' | 'taskGroup'
  filterProperties: string[]
  totalOptions?: number
  isOpen?: boolean
}

const InfoDropdownHeader: React.FC<InfoDropdownHeaderProps> = ({
  search = null,
  filterType,
  filterProperties,
  totalOptions = 3,
  isOpen = false,
  ...props
}) => {
  const {t} = useTranslation('kartavya')

  let label = ''
  let filterCount = 0
  if (filterType === 'date') {
    const fromDateKey = filterProperties[0]
    const toDateKey = filterProperties[1]

    label = t('tasks.date-filter.all-time.label')
    if (search) {
      if (search[fromDateKey] && search[toDateKey]) {
        const fromDate = moment(search[fromDateKey])
        const toDate = moment(search[toDateKey])

        if (fromDate.isSame(oneWeekAgo, 'day') && toDate.isSame(today, 'day')) {
          label = t('tasks.date-filter.past-week.label')
        } else if (fromDate.isSame(today, 'day') && toDate.isSame(today, 'day')) {
          label = t('tasks.date-filter.today.label')
        } else if (fromDate.isSame(today, 'day') && toDate.isSame(fourteenDaysFromToday, 'day')) {
          label = t('tasks.date-filter.next-14-days.label')
        } else if (fromDate.isSame(today, 'day') && toDate.isSame(thirtyDaysFromToday, 'day')) {
          label = t('tasks.date-filter.next-30-days.label')
        } else {
          label = `${formattedDate(search[fromDateKey])} - ${formattedDate(search[toDateKey])}`
        }

        filterCount = 1
      } else if (search[fromDateKey]) {
        label = t('tasks.date-filter.date-from.label', {
          fromDate: formattedDate(search[fromDateKey]),
        })

        filterCount = 1
      } else if (search[toDateKey]) {
        label = t('tasks.date-filter.date-to.label', {
          toDate: formattedDate(search[toDateKey]),
        })

        filterCount = 1
      }
    }
  } else if (filterType === 'status') {
    label = t('tasks.status-filter.all-statuses.label')
    if (
      search &&
      search[filterProperties[0]] &&
      totalOptions &&
      search[filterProperties[0]].length < totalOptions
    ) {
      label = t('tasks.status-filter.custom.label')
      filterCount = search[filterProperties[0]].length
    }
  } else if (filterType === 'taskGroup') {
    label = t('tasks.group-filter.all-group.label')
    if (
      search &&
      search[filterProperties[0]] &&
      totalOptions &&
      search[filterProperties[0]].length
    ) {
      const groupOptions = getTaskGroupOptions(t)
      const selectedGroupOptions = getSelectedTaskGroupOptions(
        search[filterProperties[0]],
        groupOptions
      )
      label = t('tasks.group-filter.custom.label')
      filterCount = selectedGroupOptions.length
    }
  }

  return (
    <Wrapper {...props}>
      <Caption mr={8}>{label}</Caption>
      {filterCount > 0 ? <BadgeIcon>{filterCount}</BadgeIcon> : null}
      <DropdownArrow data-ident="dropdown-arrow" isOpen={isOpen} />
    </Wrapper>
  )
}

const mapStateToProps = (state: Record<string, any>): Pick<InfoDropdownHeaderProps, 'search'> => ({
  search: selectSearchObj(state),
})

export default connect(mapStateToProps, null)(InfoDropdownHeader)
